"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

interface CookiePreferences {
    necessary: boolean
    analytics: boolean
    marketing: boolean
}

interface ConsentContextType {
    preferences: CookiePreferences
    hasConsented: boolean
    acceptAll: () => void
    acceptNecessary: () => void
    updatePreferences: (prefs: CookiePreferences) => void
    showModal: boolean
    setShowModal: (show: boolean) => void
}

const defaultPreferences: CookiePreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
}

const ConsentContext = createContext<ConsentContextType | null>(null)

export function useConsent() {
    const context = useContext(ConsentContext)
    if (!context) {
        throw new Error("useConsent must be used within ConsentProvider")
    }
    return context
}

export function ConsentProvider({ children }: { children: ReactNode }) {
    const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences)
    const [hasConsented, setHasConsented] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        const consent = localStorage.getItem("cookie-consent")
        if (consent) {
            try {
                const savedPreferences = JSON.parse(consent) as CookiePreferences
                setPreferences(savedPreferences)
                setHasConsented(true)
            } catch {
                // Migration from old format
                if (consent === "accepted") {
                    const allAccepted = { necessary: true, analytics: true, marketing: true }
                    localStorage.setItem("cookie-consent", JSON.stringify(allAccepted))
                    setPreferences(allAccepted)
                    setHasConsented(true)
                } else {
                    localStorage.removeItem("cookie-consent")
                }
            }
        }
        setIsLoaded(true)
    }, [])

    const saveAndApply = (prefs: CookiePreferences) => {
        setPreferences(prefs)
        setHasConsented(true)
        localStorage.setItem("cookie-consent", JSON.stringify(prefs))

        // Clean up cookies if analytics/marketing disabled
        if (!prefs.analytics) {
            cleanupAnalyticsCookies()
        }
        if (!prefs.marketing) {
            cleanupMarketingCookies()
        }
    }

    const acceptAll = () => {
        saveAndApply({ necessary: true, analytics: true, marketing: true })
        setShowModal(false)
    }

    const acceptNecessary = () => {
        saveAndApply({ necessary: true, analytics: false, marketing: false })
        setShowModal(false)
    }

    const updatePreferences = (prefs: CookiePreferences) => {
        saveAndApply(prefs)
    }

    return (
        <ConsentContext.Provider
            value={{
                preferences,
                hasConsented,
                acceptAll,
                acceptNecessary,
                updatePreferences,
                showModal,
                setShowModal,
            }}
        >
            {isLoaded ? children : null}
        </ConsentContext.Provider>
    )
}

function cleanupAnalyticsCookies() {
    // Remove GA scripts
    const gaScripts = document.querySelectorAll(
        'script[src*="googletagmanager"], script[src*="google-analytics"], script[src*="vercel"]'
    )
    gaScripts.forEach((script) => script.remove())

    // Delete GA cookies
    document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=")
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()
        if (name.startsWith("_ga") || name.startsWith("_gid") || name.startsWith("_vercel")) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        }
    })
}

function cleanupMarketingCookies() {
    // Remove marketing scripts
    const marketingScripts = document.querySelectorAll(
        'script[src*="facebook"], script[src*="linkedin"], script[src*="twitter"]'
    )
    marketingScripts.forEach((script) => script.remove())

    // Delete marketing cookies
    document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=")
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()
        if (
            name.startsWith("_fbp") ||
            name.startsWith("_fbc") ||
            name.includes("marketing")
        ) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        }
    })
}
